import resolveModule from "@app/resolveModule/frontend";
import type ApiUrlCreator from "@core-ui/ApiServices/ApiUrlCreator";
import FetchService from "@core-ui/ApiServices/FetchService";
import type Url from "@core-ui/ApiServices/Types/Url";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import useObjectUrl from "@core-ui/hooks/useObjectUrl";
import useWatch, { useWatchClient } from "@core-ui/hooks/useWatch";
import { resolveFileKind } from "@core-ui/utils/resolveFileKind";
import { useSetting } from "@ext/settings/logic/hooks";
import Theme from "@ext/Theme/Theme";
import { useCallback, useRef, useState } from "react";

export type CatalogLogo = { iconCode: string; iconColor?: string } | { emoji: string } | { src: string };

const useCatalogLogoManager = (catalogPath: string, theme: Theme) => {
	const apiUrlCreator = ApiUrlCreatorService.value;
	const { url: logo, setBlob, clear } = useObjectUrl();
	const [isLoading, setIsLoading] = useState(false);

	const getLogo = useCallback(async () => {
		setIsLoading(true);
		const url = apiUrlCreator.getLogoUrl(catalogPath, theme, true);
		const res = await FetchService.fetchInBackground(url);

		setIsLoading(false);
		if (!res.ok || !res.body) return null;
		const blob = new Blob([res.body as unknown as BlobPart], {
			type: resolveFileKind(res.body as unknown as Buffer),
		});

		return blob;
	}, [catalogPath, theme]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: expected
	const refreshLogo = useCallback(async () => {
		const blob = await getLogo();
		if (blob) setBlob(blob);
		else clear();
	}, [catalogPath, theme]);

	useWatchClient(async () => {
		if (catalogPath) await refreshLogo();
	}, [getLogo, catalogPath, theme]);

	const resetState = useCallback(() => {
		clear();
		setIsLoading(false);
	}, [clear]);

	return { logo: logo ?? "", isLoading, refreshLogo, resetState };
};

export const useCatalogLogo = (catalogPath?: string) => {
	const {
		isLoading: isLoadingDark,
		logo: initialDarkLogo,
		refreshLogo: refreshDarkLogo,
	} = useCatalogLogoManager(catalogPath, Theme.dark);

	const {
		isLoading: isLoadingLight,
		logo: initialLightLogo,
		refreshLogo: refreshLightLogo,
	} = useCatalogLogoManager(catalogPath, Theme.light);

	const [lightLogo, setLightLogo] = useState<string | null>(initialLightLogo);
	const [darkLogo, setDarkLogo] = useState<string | null>(initialDarkLogo);

	useWatch(() => {
		setLightLogo(initialLightLogo);
		setDarkLogo(initialDarkLogo);
	}, [initialDarkLogo, initialLightLogo]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: expected
	const refreshState = useCallback(async () => {
		await refreshDarkLogo();
		await refreshLightLogo();
	}, []);

	return {
		isLoadingDark,
		isLoadingLight,
		lightLogo,
		darkLogo,
		refreshState,
	};
};

export type CatalogLogoLocation = Exclude<CatalogLogo, { src: string }> | { url: Url };

const findThemeCatalogLogo = async (
	apiUrlCreator: ApiUrlCreator,
	catalogName: string,
	theme: Theme,
): Promise<CatalogLogoLocation | null> => {
	const res = await FetchService.fetchInBackground(apiUrlCreator.catalogLogoExist(catalogName, theme));
	if (!res.ok || !res.body) return null;

	const data: { isExist: boolean; iconCode?: string; iconColor?: string; emoji?: string } = await res.json();
	if (!data.isExist) return null;
	if (data.iconCode) return { iconCode: data.iconCode, iconColor: data.iconColor };
	if (data.emoji) return { emoji: data.emoji };
	return { url: apiUrlCreator.getLogoUrl(catalogName, theme, true) };
};

export const findCatalogLogo = async (
	apiUrlCreator: ApiUrlCreator,
	catalogName: string,
	theme: Theme,
): Promise<CatalogLogoLocation | null> =>
	(await findThemeCatalogLogo(apiUrlCreator, catalogName, theme)) ??
	(theme !== Theme.light ? await findThemeCatalogLogo(apiUrlCreator, catalogName, Theme.light) : null);

export const useGetCatalogLogoSrc = (catalogName: string, deeps = []) => {
	const [theme] = useSetting("general.theme");
	const apiUrlCreator = ApiUrlCreatorService.value;
	const useImage = resolveModule("useImage");
	const [urlToFetch, setUrlToFetch] = useState<Url>();
	const [logo, setLogo] = useState<CatalogLogo>(null);
	const activeRequestId = useRef(0);

	useWatchClient(async () => {
		activeRequestId.current += 1;
		const requestId = activeRequestId.current;

		if (!catalogName) {
			// This branch runs synchronously inside render (useWatchClient). Even a no-op setState here forces a
			// render-phase re-render, which makes React drop `useSetting`'s snapshot update for this card.
			if (logo !== null) setLogo(null);
			if (urlToFetch !== undefined) setUrlToFetch(undefined);
			return;
		}

		const found = await findCatalogLogo(apiUrlCreator, catalogName, theme);

		if (requestId !== activeRequestId.current) return;

		if (!found) {
			setLogo(null);
			setUrlToFetch(undefined);
		} else if ("url" in found) {
			setLogo(null);
			setUrlToFetch(found.url);
		} else {
			setLogo(found);
			setUrlToFetch(undefined);
		}
	}, [theme, catalogName, ...deeps]);

	const src = useImage(urlToFetch, deeps);

	return { logo: logo ?? (src ? { src } : null) } as { logo: CatalogLogo };
};
